<template>
  <div class="comment-content">
    <RepliedComment
      v-if="comment.repliedOnComment"
      :repliedOnComment="comment.repliedOnComment"
    />
    <BaseSubHeading
      size="medium"
      weight="400"
    >
      <CommentTimestampText
        v-if="demoVideoTimestamp"
        :timestamp="demoVideoTimestamp"
        :history="isPreviousDemoVideo || isDemoVideoDeleted"
        :deleted="isDemoVideoDeleted"
        :label="demoVideoTimestampLabel"
      />
      <ReferencedText
        :html="comment.structuredDescription.html || comment.structuredDescription.text"
      />
    </BaseSubHeading>
    <div
      v-if="comment.fileMetadata?.length"
      class="comment-content__files"
    >
      <FileItem
        v-for="(file, index) in comment.fileMetadata"
        :key="index"
        :file="file"
        :files="commentsFiles"
        :projectScoped="projectScoped"
        fancyboxGroup="comment_tab_fancybox"
      />
    </div>
    <slot name="after" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"

import CommentTimestampText from "@/components/Comment/CommentTimestampText.vue"
import RepliedComment from "@/components/Comment/RepliedComment.vue"
import FileItem from "@/components/File/FileItem.vue"
import { ReferencedText } from "@/components/Reference"

import {
  getDemoVideoVersionLabel,
  isDemoVideoIdDeleted,
  isPreviousDemoVideoId
} from "@/composables/useDemoVideo"
import { Comment, FileMetaData } from "@/models"
import { formatTimestamp } from "@/utils/timestampUtil"

import type { DemoVideoVersionInfo } from "@/composables/useDemoVideo"

const props = defineProps({
  comment: {
    required: true,
    type: Object as () => Comment
  },
  comments: {
    default: () => [],
    type: Array as () => Comment[]
  },
  demoVideoId: {
    default: "",
    type: String
  },
  demoVideoVersionMap: {
    default: () => ({}),
    type: Object as () => Record<string, DemoVideoVersionInfo>
  },
  projectScoped: {
    type: Boolean,
    default: true
  }
})

const commentsFiles = computed(() => {
  return (props.comments ?? []).reduce((acc: FileMetaData[], comment: Comment) => {
    if (comment.fileMetadata?.length) {
      acc = [...comment.fileMetadata, ...acc]
    }
    return acc
  }, [])
})

const demoVideoTimestamp = computed(() => {
  return props.comment.demoVideoTimestamp !== null && props.comment.demoVideoTimestamp >= 0
    ? formatTimestamp(props.comment.demoVideoTimestamp)
    : ""
})

const isPreviousDemoVideo = computed(() =>
  isPreviousDemoVideoId(props.demoVideoId, props.demoVideoVersionMap)
)
const isDemoVideoDeleted = computed(() =>
  isDemoVideoIdDeleted(props.demoVideoId, props.demoVideoVersionMap)
)
const demoVideoTimestampLabel = computed(() => {
  if (!isPreviousDemoVideo.value && !isDemoVideoDeleted.value) return ""

  const versionLabel = getDemoVideoVersionLabel(props.demoVideoId, props.demoVideoVersionMap)

  return isDemoVideoDeleted.value ? `${versionLabel} Deleted` : versionLabel
})
</script>

<style scoped lang="scss">
@import "@/assets/scss/utils/mixins/_ellipsis";
@import "@/assets/scss/utils/mixins/_flex";
</style>
